import _ from "lodash";
import { isNonProduction } from "./utils/env";
import qs from "query-string";
import { Redirect } from "react-router-dom";

import { Login } from "./pages/auth/Login";
import { NoMatch } from "./pages/error/NoMatch";

import { sessionPath } from "./config/GetPagePath";
import { Role } from "./config/Roles";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { Components } from "./pages/components/Components";
import { Dashboard } from "./pages/dashboard/Dashboard";
import { DashboardID } from "./pages/dashboard/DashboardID";
import { HelpRequestComplete } from "./pages/helpRequestForm/completeRequest";
import { HelpRequestStart } from "./pages/helpRequestForm/startRequest";
import { HelpRequestWelcome } from "./pages/helpRequestForm/welcome";
import { FAQ } from "./pages/helpSupport/FAQ";
import { LogsArchiveSession } from "./pages/logs/LogsArchiveSession";
import { LogsCampaign } from "./pages/logs/LogsCampaign";
import { LogsPersonal } from "./pages/logs/LogsPersonal";
import { LogsSupport } from "./pages/logs/LogsSupport";
import { LogsTeam } from "./pages/logs/LogsTeam";
import { Maintenance } from "./pages/maintenance";
import { SessionsCampaignOverview } from "./pages/sessions/sessionOverview/SessionsCampaignOverview";
import { SessionsPersonalOverview } from "./pages/sessions/sessionOverview/SessionsPersonalOverview";
import { SessionsSupportOverview } from "./pages/sessions/sessionOverview/SessionsSupportOverview";
import { SessionsTeamOverview } from "./pages/sessions/sessionOverview/SessionsTeamOverview";
import { SessionsCoachingForm } from "./pages/sessions/SessionsCoachingForm";
import { SessionsCoachingSummary } from "./pages/sessions/SessionsCoachingSummary";
import { SessionsConversationalForm } from "./pages/sessions/SessionsConversationalForm";
import { SessionsDetails } from "./pages/sessions/SessionsDetails";
import { SessionsEditCheckpoints } from "./pages/sessions/SessionsEditCheckpoints";
import { SessionsForm } from "./pages/sessions/SessionsForm";
import { SessionsGrowForm } from "./pages/sessions/SessionsGrowForm";
import { SessionsGrowFormDetails } from "./pages/sessions/SessionsGrowFormDetails";
import { SessionsReviewSession } from "./pages/sessions/SessionsReviewSession";
import { SessionsReviewSummary } from "./pages/sessions/SessionsReviewSummary";
import { SessionsSetCheckpoints } from "./pages/sessions/SessionsSetCheckpoints";
import { SessionsUpdateCheckpoints } from "./pages/sessions/SessionsUpdateCheckpoints";
import { ChangePassword } from "./pages/settings/ChangePassword";
import { EditSubject } from "./pages/settings/EditSubject";
import { EditUser } from "./pages/settings/EditUser";
import { SubjectManagement } from "./pages/settings/SubjectManagement";
import { UserManagement } from "./pages/settings/UserManagement";
import { OverallCoachingReport } from "./pages/settings/OverallCoachingReport";
import { TeamCampaign } from "./pages/teams/TeamCampaign";
import { TeamPersonalProfile } from "./pages/teams/TeamPersonalProfile";
import { Teams } from "./pages/teams/Teams";
import { TeamSupportAdmin } from "./pages/teams/TeamSupportAdmin";
import { WarningLetters } from "./pages/warningLetters";
import { WarningSession } from "./pages/warningLetters/WarningSession";

import { NudgeLogAnalytics, NudgeLogSession, NudgeLogSessions, NudgeLogUser } from "./pages/nudgeLog";
import { PiiDetectionLogs, PiiOccurrences } from "./pages/piiAdmin";
import { PromptTemplateFormPage } from "./pages/promptTemplate/PromptTemplateForm";
import { PromptTemplateList } from "./pages/promptTemplate/PromptTemplateList";
import { RedirectLogin } from "./pages/redirectLogin/RedirectLogin";
import CampaignManagement from "./pages/settings/CampaignManagement";
import RegionManagement from "./pages/settings/RegionManagement";
import { FeedbackOutro } from "./pages/userFeedback/components/Outro/FeedbackOutro";
import { UserFeedbackForm } from "./pages/userFeedback/UserFeedbackForm";

import ResetSecurityQuestion from "./pages/auth/ResetSecurityQuestion";
import SetSecurityQuestion from "./pages/auth/SetSecurityQuestion";
import ChangeSecurityQuestion from "./pages/settings/ChangeSecurityQuestion";

const createRoute = options => {
  if (!options.path) {
    console.error("please provide path in the route");
  }

  return Object.assign({}, options, {
    /* utils to generate path */
    getPath(params, queryString) {
      const regex = /:([A-Za-z_]+)/g;
      const query = queryString ? `?${qs.stringify(queryString)}` : "";
      const template = _.template(options.path, {
        interpolate: regex,
        evaluate: regex,
      });

      // generatePath throws error when params does not fulfilled the path's params
      return template(params) + query;
    },
  });
};

const defaultPageTitle = "FLASH COACH";

export const routes = {
  maintenance: createRoute({
    path: "/maintenance",
    component: <Maintenance title={defaultPageTitle} />,
    exact: true,
    title: "Maintenance",
    isPublic: true,
  }),
  login: createRoute({
    path: "/",
    component: <Login title={defaultPageTitle} />,
    exact: true,
    title: "Login",
    isPublic: true,
  }),
  forgotPassword: createRoute({
    path: "/forgotPassword",
    component: <ForgotPassword title={defaultPageTitle} />,
    exact: true,
    title: "Forgot Password",
    isPublic: true,
  }),
  resetPassword: createRoute({
    path: "/resetPassword/:id",
    component: <ResetPassword title={defaultPageTitle} />,
    exact: true,
    title: "Reset Password",
    isPublic: true,
  }),
  resetSecurityQuestion: createRoute({
    path: "/security-questions/reset",
    component: <ResetSecurityQuestion title={`${defaultPageTitle} - Security Reset`} />,
    exact: true,
    title: "Reset Security Question ",
    isPublic: true,
  }),
  setupSecurityQuestion: createRoute({
    path: "/security-question/setup",
    component: <SetSecurityQuestion title={`${defaultPageTitle} - Security Setup`} />,
    exact: true,
    title: "Setup Security Question ",
    isPublic: true,
  }),

  dashboard: createRoute({
    path: " https://app.powerbi.com/groups/827b0ecc-37be-4917-86fa-d3fc7aa80e17/reports/72b6dbf8-8e84-4f4e-b643-7347a460890f/ReportSection?experience=power-bi ",
    component: <Dashboard title={`${defaultPageTitle} - Dashboard`} />,
    exact: true,
    title: "Dashvoard",
    permission: [
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COE_ADMIN,
      Role.EXECUTIVE,
      Role.COORDINATOR,
      Role.CEO,
    ],
  }),

  scoresheet: createRoute({
    path: "/dashboard",
    component: <Dashboard title={`${defaultPageTitle} - Scoresheet`} />,
    exact: true,
    title: "Scoresheet",
    permission: [
      Role.COACH,
      Role.COACHEE,
      Role.CAMPAIGN_MANAGER,
      Role.COE_ADMIN,
      Role.EXECUTIVE,
      Role.COORDINATOR,
      Role.CEO,
    ],
  }),
  scoresheetID: createRoute({
    path: "/dashboard/:id",
    component: <DashboardID title={`${defaultPageTitle} - Scoresheet`} />,
    exact: true,
    title: "Scoresheet",
    permission: [
      Role.COACH,
      Role.COACHEE,
      Role.CAMPAIGN_MANAGER,
      Role.COE_ADMIN,
      Role.EXECUTIVE,
      Role.COORDINATOR,
      Role.CEO,
    ],
  }),
  settings: createRoute({
    path: "/settings/changePassword",
    component: (
      <ChangePassword title={`${defaultPageTitle} - Change password`} />
    ),
    exact: true,
    title: "Change password",
    permission: [],
  }),
  settingsChangeSecurityQuestion: createRoute({
    path: "/settings/ChangeSecurityQuestion",
    component: (
      <ChangeSecurityQuestion title={`${defaultPageTitle} - Change security question`} />
    ),
    exact: true,
    title: "Change Security Question",
    permission: [],
  }),
  settingsUserManagement: createRoute({
    path: "/settings/userManagement",
    component: (
      <UserManagement title={`${defaultPageTitle} - User Management`} />
    ),
    exact: true,
    title: "User Management",
    permission: [
      Role.SUPER_ADMIN,
      Role.IT_MANAGER,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
      Role.COE_ADMIN,
      Role.EXECUTIVE,
    ],
  }),
  settingsSubjectManagement: createRoute({
    path: "/settings/subjectManagement",
    component: (
      <SubjectManagement title={`${defaultPageTitle} - Subject Management`} />
    ),
    exact: true,
    title: "Subject Management",
    permission: [
      Role.SUPER_ADMIN,
      Role.IT_MANAGER,
      Role.CAMPAIGN_MANAGER,
      Role.SUPPORT_ADMIN,
      Role.COE_ADMIN
    ],
  }),
  settingsUserManagementEditUser: createRoute({
    path: "/settings/userManagement/editUser/:id",
    component: (
      <EditUser title={`${defaultPageTitle} - User Management - Edit`} />
    ),
    exact: true,
    title: "User Management",
    permission: [
      Role.SUPER_ADMIN,
      Role.IT_MANAGER,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
      Role.COE_ADMIN,
      Role.EXECUTIVE,
    ],
  }),
  settingsSubjectManagementEditSubject: createRoute({
    path: "/settings/subjectManagement/editSubject/:id",
    component: (
      <EditSubject title={`${defaultPageTitle} - Subject Management - Edit`} />
    ),
    exact: true,
    title: "Subject Management",
    permission: [
      Role.SUPER_ADMIN,
      Role.IT_MANAGER,
      Role.CAMPAIGN_MANAGER,
      Role.SUPPORT_ADMIN,
      Role.COE_ADMIN
    ],
  }),
  settingsCampaignManagement: createRoute({
    path: "/settings/campaignManagement",
    component: (
      <CampaignManagement title={`${defaultPageTitle} - Campaign Management`} />
    ),
    exact: true,
    title: "Campaign Management",
    permission: [
      Role.SUPER_ADMIN,
      Role.SUPPORT_ADMIN
    ]
  }),
  settingsRegionManagement: createRoute({
    path: "/settings/regionManagement",
    component: (
      <RegionManagement title={`${defaultPageTitle} - Campaign Management`} />

    ),
    exact: true,
    title: "Campaign Management",
    permission: [
      Role.SUPER_ADMIN,
      Role.SUPPORT_ADMIN
    ]
  }),
  overallCoachingReport: createRoute({
    path: "/settings/overallCoachingReport",
    component: (
      <OverallCoachingReport title={`${defaultPageTitle} - Overall Coaching Report`} />
    ),
    exact: true,
    title: "Overall Coaching Report",
    permission: [
      Role.SUPPORT_ADMIN,
    ]
  }),
  warningLetters: createRoute({
    path: "/warningLetters",
    component: <WarningLetters title="Warning Letters" />,
    exact: true,
    title: "Warning Letters",
    permission: [Role.HR],
  }),
  warningLettersSession: createRoute({
    path: "/warningLetters/session/:id",
    component: <WarningSession title="Warning Letters - Session" />,
    exact: true,
    title: "Warning Letters",
    permission: [Role.HR],
  }),
  // AI coaching admin tooling (prompt templates, nudge logs) is blocked in
  // production outright; see routeList below.
  promptTemplateList: createRoute({
    path: "/prompt-templates",
    component: <PromptTemplateList title={`${defaultPageTitle} - Prompt Templates`} />,
    exact: true,
    title: "Prompt Templates",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
    nonProdOnly: true,
  }),
  promptTemplateCreate: createRoute({
    path: "/prompt-templates/create",
    component: <PromptTemplateFormPage title={`${defaultPageTitle} - Create Prompt Template`} />,
    exact: true,
    title: "Create Prompt Template",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
    nonProdOnly: true,
  }),
  promptTemplateEdit: createRoute({
    path: "/prompt-templates/:id/edit",
    component: <PromptTemplateFormPage title={`${defaultPageTitle} - Edit Prompt Template`} />,
    exact: true,
    title: "Edit Prompt Template",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
    nonProdOnly: true,
  }),
  nudgeLogSession: createRoute({
    path: "/nudge-logs/session/:sessionId",
    component: <NudgeLogSession title={`${defaultPageTitle} - Nudge Logs - Session`} />,
    exact: true,
    title: "Nudge Logs - Session",
    nonProdOnly: true,
  }),
  nudgeLogUser: createRoute({
    path: "/nudge-logs/user/:userId",
    component: <NudgeLogUser title={`${defaultPageTitle} - Nudge Logs - User`} />,
    exact: true,
    title: "Nudge Logs - User",
    permission: [
      Role.COACH,
      Role.SUPPORT_ADMIN,
      Role.COORDINATOR,
    ],
    nonProdOnly: true,
  }),
  nudgeLogAnalytics: createRoute({
    path: "/nudge-logs/analytics",
    component: <NudgeLogAnalytics title={`${defaultPageTitle} - Nudge Logs Analytics`} />,
    exact: true,
    title: "Nudge Logs Analytics",
    nonProdOnly: true,
  }),
  nudgeLogSessions: createRoute({
    path: "/nudge-logs/sessions",
    component: <NudgeLogSessions title={`${defaultPageTitle} - Nudge Logs Sessions`} />,
    exact: true,
    title: "Nudge Logs Sessions",
    nonProdOnly: true,
  }),
  piiDetectionLogs: createRoute({
    path: "/pii-admin/detection-logs",
    component: <PiiDetectionLogs title={`${defaultPageTitle} - PII Detection Logs`} />,
    exact: true,
    title: "PII Detection Logs",
    // Blocked in production outright; non-prod stays open to any
    // authenticated user.
    nonProdOnly: true,
  }),
  piiOccurrences: createRoute({
    path: "/pii-admin/occurrences",
    component: <PiiOccurrences title={`${defaultPageTitle} - PII Occurrences`} />,
    exact: true,
    title: "PII Occurrences",
    // Shows raw decrypted PII values, so unlike piiDetectionLogs this is
    // restricted to admin roles even in non-prod rather than any
    // authenticated user.
    permission: [Role.SUPER_ADMIN, Role.SUPPORT_ADMIN],
    nonProdOnly: true,
  }),
  sessions: createRoute({
    path: "/sessions",
    component: <Redirect to={sessionPath()} />,
    exact: true,
    title: "Sessions",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
    ],
  }),
  sessionsCampaign: createRoute({
    path: "/sessions/campaign",
    component: (
      <SessionsCampaignOverview title={`${defaultPageTitle} - Sessions`} />
    ),
    exact: true,
    title: "Sessions",
    permission: [Role.CAMPAIGN_MANAGER, Role.COORDINATOR],
  }),
  sessionsCampaignIndividual: createRoute({
    path: "/sessions/campaign/:id",
    component: (
      <SessionsCampaignOverview title={`${defaultPageTitle} - Sessions`} />
    ),
    exact: true,
    title: "Sessions",
    permission: [Role.CAMPAIGN_MANAGER, Role.COORDINATOR],
  }),
  sessionsSupport: createRoute({
    path: "/sessions/support",
    component: (
      <SessionsSupportOverview title={`${defaultPageTitle} - Sessions`} />
    ),
    exact: true,
    title: "Sessions",
    permission: [Role.SUPPORT_ADMIN],
  }),
  sessionsTeams: createRoute({
    path: "/sessions/teams",
    component: (
      <SessionsTeamOverview title={`${defaultPageTitle} - Sessions`} />
    ),
    exact: true,
    title: "Sessions",
    permission: [Role.COACH, Role.CAMPAIGN_MANAGER, Role.COORDINATOR],
  }),
  sessionsTeamsIndividual: createRoute({
    path: "/sessions/teams/:id",
    component: (
      <SessionsTeamOverview title={`${defaultPageTitle} - Sessions`} />
    ),
    exact: true,
    title: "Sessions",
    permission: [Role.COACH, Role.CAMPAIGN_MANAGER, Role.COORDINATOR],
  }),
  sessionsPersonal: createRoute({
    path: "/sessions/personal",
    component: (
      <SessionsPersonalOverview title={`${defaultPageTitle} - Sessions`} />
    ),
    exact: true,
    title: "Sessions",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
    ],
  }),
  sessionsForm: createRoute({
    path: "/sessions/form/:id",
    component: <SessionsForm />,
    exact: true,
    title: "Sessions",
    permission: [Role.COACH, Role.CAMPAIGN_MANAGER, Role.COORDINATOR],
  }),
  sessionsDetails: createRoute({
    path: "/sessions/details/:id",
    component: <SessionsDetails />,
    exact: true,
    title: "Sessions",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
  }),
  sessionsConversationalForm: createRoute({
    path: "/sessions/conversational/:id",
    component: <SessionsConversationalForm />,
    exact: true,
    title: "Conversational Form",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
  }),
  sessionsGrowForm: createRoute({
    path: "/sessions/grow/:id",
    component: <SessionsGrowForm />,
    exact: true,
    title: "Grow Form",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
  }),
  sessionsGrowFormDetails: createRoute({
    path: "/sessions/grow/details/:id",
    component: <SessionsGrowFormDetails />,
    exact: true,
    title: "Grow Form Details",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
  }),
  sessionsCoachingForm: createRoute({
    path: "/sessions/coaching/:id",
    component: <SessionsCoachingForm />,
    exact: true,
    title: "Sessions - Coaching Form",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
  }),
  sessionsCoachingSummary: createRoute({
    path: "/sessions/coaching/summary/:id",
    component: <SessionsCoachingSummary />,
    exact: true,
    title: "Sessions - Coaching Summary",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
  }),
  sessionsSetCheckpoints: createRoute({
    path: "/sessions/set-checkpoints/:id",
    component: <SessionsSetCheckpoints />,
    exact: true,
    title: "Sessions - Set checkpoints",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
  }),
  sessionsEditCheckpoints: createRoute({
    path: "/sessions/edit-checkpoints/:id",
    component: <SessionsEditCheckpoints />,
    exact: true,
    title: "Sessions - Edit checkpoints",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
  }),
  sessionsUpdateCheckpoints: createRoute({
    path: "/sessions/update-checkpoints/:id",
    component: <SessionsUpdateCheckpoints />,
    exact: true,
    title: "Sessions - Update checkpoints",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
  }),
  sessionsReviewSession: createRoute({
    path: "/sessions/review-session/:id",
    component: <SessionsReviewSession />,
    exact: true,
    title: "Sessions - Review session",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
  }),
  sessionsReviewSummary: createRoute({
    path: "/sessions/review-summary/:id",
    component: <SessionsReviewSummary />,
    exact: true,
    title: "Sessions - Review summary",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
      Role.SUPPORT_ADMIN,
    ],
  }),
  sessionsArchiveSession: createRoute({
    path: "/sessions/archive-session/:id",
    component: <LogsArchiveSession />,
    exact: true,
    title: "Sessions - Archive session",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COE_ADMIN,
      Role.SUPPORT_ADMIN,
      Role.HR,
      Role.COORDINATOR,
    ],
  }),
  teams: createRoute({
    path: "/teams",
    component: <Teams title={`${defaultPageTitle} - Teams`} />,
    exact: true,
    title: "Teams",
    permission: [Role.COACH, Role.CAMPAIGN_MANAGER, Role.COORDINATOR],
  }),
  campaignTeam: createRoute({
    path: "/teams/campaign",
    component: <TeamCampaign title={`${defaultPageTitle} - Teams`} />,
    exact: true,
    title: "Teams",
    permission: [Role.CAMPAIGN_MANAGER],
  }),
  coordinatorTeam: createRoute({
    path: "/teams/coordinator",
    component: <Teams title={`${defaultPageTitle} - Teams`} />,
    exact: true,
    title: "Teams",
    permission: [Role.COORDINATOR],
  }),
  supportAdminTeam: createRoute({
    path: "/teams/supportAdmin",
    component: <TeamSupportAdmin title={`${defaultPageTitle} - Teams`} />,
    exact: true,
    title: "Teams",
    permission: [Role.SUPPORT_ADMIN],
  }),
  profile: createRoute({
    path: "/profile/",
    component: <TeamPersonalProfile title={`${defaultPageTitle} - Teams`} />,
    exact: true,
    title: "Profile",
    permission: [Role.COACHEE],
  }),
  teamPersonalProfile: createRoute({
    path: "/teams/details/:id",
    component: <TeamPersonalProfile title={`${defaultPageTitle} - Teams`} />,
    exact: true,
    title: "Personal profile",
    permission: [
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.SUPPORT_ADMIN,
      Role.SUPER_ADMIN,
      Role.COORDINATOR,
    ],
  }),
  logs: createRoute({
    path: "/logs",
    // component: <Logs />,
    component: <Redirect to="/logs/team" />,
    exact: true,
    title: "Logs",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
    ],
  }),

  logsArchiveSession: createRoute({
    path: "/logs/archive-session/:id",
    component: <LogsArchiveSession />,
    exact: true,
    title: "Logs - Archive session",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COE_ADMIN,
      Role.SUPPORT_ADMIN,
      Role.HR,
      Role.COORDINATOR,
    ],
  }),
  logsTeam: createRoute({
    path: "/logs/team",
    component: <LogsTeam title={`${defaultPageTitle} - logs`} />,
    exact: true,
    title: "Logs",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
    ],
  }),
  logsCampaign: createRoute({
    path: "/logs/campaign",
    component: <LogsCampaign title={`${defaultPageTitle} - logs`} />,
    exact: true,
    title: "Logs",
    permission: [Role.CAMPAIGN_MANAGER, Role.COORDINATOR],
  }),
  logsSupport: createRoute({
    path: "/logs/support",
    component: <LogsSupport title={`${defaultPageTitle} - logs`} />,
    exact: true,
    title: "Logs",
    permission: [Role.SUPPORT_ADMIN],
  }),
  logsPersonal: createRoute({
    path: "/logs/personal",
    component: <LogsPersonal title={`${defaultPageTitle} - logs`} />,
    exact: true,
    title: "Logs",
    permission: [
      Role.COACHEE,
      Role.COACH,
      Role.CAMPAIGN_MANAGER,
      Role.COORDINATOR,
    ],
  }),
  faq: createRoute({
    path: "/help-support",
    component: <FAQ title={`${defaultPageTitle} - Help and support`} />,
    exact: true,
    title: "Help & Support",
    isPublic: true,
  }),
  UserFeedbackForm: createRoute({
    path: "/user-feedback",
    component: <UserFeedbackForm />,
    exact: true,
    title: "User Feedback",
  }),
  FeedbackOutro: createRoute({
    path: "/user-feedback/feedback-success",
    component: <FeedbackOutro />,
    exact: true,
    title: "User Feedback",
  }),
  componentsPage: createRoute({
    path: "/components",
    component: <Components title={`${defaultPageTitle} - Component List`} />,
    exact: true,
    title: "Components",
    isPublic: true,
  }),
  helpRequestWelcome: createRoute({
    path: "/help-request/welcome",
    component: <HelpRequestWelcome />,
    exact: true,
    title: "Help Request Form",
    isPublic: true,
  }),
  helpRequestStart: createRoute({
    path: "/help-request/:key/form",
    component: <HelpRequestStart />,
    exact: true,
    title: "Help Request Form",
    isPublic: true,
  }),
  helpRequestComplete: createRoute({
    path: "/help-request/:key/submitted",
    component: <HelpRequestComplete />,
    exact: true,
    title: "Help Request Form",
    isPublic: true,
  }),
  redirectLogin: createRoute({
    path: "/redirect-login",
    component: <RedirectLogin title={defaultPageTitle} />,
    exact: true,
    isPublic: true
  }),
  noMatch: createRoute({
    path: "*",
    component: <NoMatch title={`${defaultPageTitle} - 404`} />,
    title: "Page Not Found",
  }),
};

export const routeList = _.values(routes).filter(
  route => !route.nonProdOnly || isNonProduction()
);
